import { colorsFormValuesToPalette } from "containers/admin/clients/client/theme-configuration-page/brand-identity/colors-tab/colors-tab.helpers";
import {
  BRAND_SHADE_STOPS,
  isHexColor,
  NEUTRAL_SHADE_STOPS,
} from "containers/admin/clients/client/theme-configuration-page/helpers/color-shades";

import type { SavedTheme } from "hooks/api/use-theme";
import type { BeeFont } from "types/bee";
import type {
  APIFormatBaseTypographyInput,
  APIFormatThemeLayoutData,
  APILayoutResponsivePadding,
  APIPaddingOptions,
  APIParagraphTypographyInput,
  ColorsFormData,
} from "containers/admin/clients/client/theme-page/preview/preview.types";
import type { ThemePalette } from "containers/admin/clients/client/theme-configuration-page/theme-configuration.types";
import type {
  ButtonValues,
  ButtonWidthMode,
  EmailConfigurationFormValues,
  LayoutValues,
  PaddingValues,
  ResponsivePaddingValues,
  ThemeColorOption,
  TypographySectionName,
  TypographyValues,
} from "containers/admin/clients/client/theme-configuration-page/configuration/email-configuration/email-configuration.types";

import {
  BRAND_COLOR_FIELDS,
  DEFAULT_COLORS_FORM_VALUES,
  UTILITY_COLOR_FIELDS,
} from "containers/admin/clients/client/theme-configuration-page/brand-identity/colors-tab/colors-tab.constants";
import {
  COLOR_NONE_VALUE,
  DEFAULT_EMAIL_THEME,
  DEFAULT_FIXED_BUTTON_WIDTH,
  FILL_BUTTON_WIDTH,
  FIXED_BUTTON_WIDTH_STEP,
  FONT_WEIGHT_LABELS,
  FONT_WEIGHTS,
  LINE_HEIGHT_OPTIONS,
  MAX_FIXED_BUTTON_WIDTH,
  MIN_FIXED_BUTTON_WIDTH,
  TYPOGRAPHY_SECTIONS,
} from "containers/admin/clients/client/theme-configuration-page/configuration/email-configuration/email-configuration.constants";

import THEME_DEFAULT_DATA from "containers/admin/clients/client/theme-page/theme-default-data.json";

/** The theme's own palette, or the one Brand identity starts from when it has never been saved. */
export const getThemePalette = (theme: SavedTheme | undefined): ThemePalette =>
  theme?.palette ?? colorsFormValuesToPalette(DEFAULT_COLORS_FORM_VALUES);

/** Every colour the palette defines, in Brand identity's order. */
export const getPaletteColorOptions = ({ brand, neutral, utility }: ThemePalette): ThemeColorOption[] => [
  ...BRAND_COLOR_FIELDS.flatMap(({ name, label }) => {
    const color = brand[name];

    return color
      ? BRAND_SHADE_STOPS.map((stop) => ({ label: `${label}/${stop}`, value: `${name}-${stop}`, color: color.shades[stop] }))
      : [];
  }),
  ...NEUTRAL_SHADE_STOPS.map((stop) => ({
    label: `Neutral-black/${stop}`,
    value: `neutral-${stop}`,
    color: neutral.black.shades[stop],
  })),
  ...UTILITY_COLOR_FIELDS.map(({ name, label }) => ({ label, value: name, color: utility[name] })),
];

/** A colour is saved as its hex, so the option it came from is found again by value. */
export const findColorOption = (options: ThemeColorOption[], color: string) =>
  options.find((option) => option.color.toUpperCase() === color.toUpperCase());

/**
 * The legacy page saved most colours as a key of its own `colors` (`intense`, `primary`); they are read
 * as the hex they paint, so they show as that colour here and are saved as hex from now on.
 */
const toHexColor = (value: string | undefined, colors: Partial<ColorsFormData>) => {
  if (!value || value === COLOR_NONE_VALUE) {
    return value ?? "";
  }

  const hex = value.startsWith("#") ? value : colors[value];

  return isHexColor(hex) ? hex.toUpperCase() : expandShortHex(hex);
};

/** `#ABC` paints in CSS and BEE alike, so it is kept as its six-digit form rather than dropped. */
const expandShortHex = (hex: string | undefined) =>
  hex && /^#[0-9A-Fa-f]{3}$/.test(hex) ? `#${[...hex.slice(1)].map((digit) => digit + digit).join("")}`.toUpperCase() : "";

/** Same backwards-compatible bucketing the legacy page applies to line heights saved before the select. */
const toLineHeightOption = (lineHeight: string) => {
  if (LINE_HEIGHT_OPTIONS.some(({ value }) => value === lineHeight)) {
    return lineHeight;
  }

  const value = Number.parseFloat(lineHeight);

  if (Number.isNaN(value) || value < 1.4) {
    return "1.2";
  }

  if (value < 1.7) {
    return "1.5";
  }

  return value < 1.9 ? "1.8" : "2.0";
};

const toPaddingValues = (padding: APIPaddingOptions): PaddingValues => ({
  ...padding,
  isCustom: padding.left !== padding.right || padding.top !== padding.bottom,
});

const toResponsivePaddingValues = ({ mobile, desktop }: APILayoutResponsivePadding): ResponsivePaddingValues => ({
  mobile: toPaddingValues(mobile),
  desktop: toPaddingValues(desktop),
});

const toButtonWidthMode = (width: string): ButtonWidthMode => {
  if (!width || width === "auto") {
    return "auto";
  }

  return width === FILL_BUTTON_WIDTH ? "fill" : "fixed";
};

export const themeToFormValues = (theme: SavedTheme | undefined): EmailConfigurationFormValues => {
  const colors: Partial<ColorsFormData> = { ...THEME_DEFAULT_DATA.colors, ...theme?.colors };
  // Merged a level down too: a theme saved in part elsewhere may hold a section without its nested parts.
  const button = {
    ...DEFAULT_EMAIL_THEME.button,
    ...theme?.button,
    border: { ...DEFAULT_EMAIL_THEME.button.border, ...theme?.button?.border },
  };
  const layout = {
    contentArea: theme?.layout?.contentArea ?? DEFAULT_EMAIL_THEME.layout.contentArea,
    contentBlock: { ...DEFAULT_EMAIL_THEME.layout.contentBlock, ...theme?.layout?.contentBlock },
  };
  const { contentBlock } = layout;

  const toTypographyValues = (name: TypographySectionName): TypographyValues => {
    const section: APIParagraphTypographyInput = {
      alignment: "",
      ...DEFAULT_EMAIL_THEME.typography[name],
      ...theme?.typography?.[name],
    };

    return {
      fontFamily: section.fontFamily ?? "",
      fallbackFontFamily: section.fallbackFontFamily ?? "",
      fontSize: section.fontSize ?? "",
      lineHeight: toLineHeightOption(section.lineHeight ?? ""),
      fontWeight: section.fontWeight ?? "",
      textColor: toHexColor(section.textColor, colors),
      alignment: section.alignment || "left",
      linkColor: toHexColor(section.linkColor, colors),
    };
  };

  return {
    typography: Object.fromEntries(
      TYPOGRAPHY_SECTIONS.map(({ name }) => [name, toTypographyValues(name)]),
    ) as Record<TypographySectionName, TypographyValues>,
    button: {
      padding: toPaddingValues(button.padding),
      widthMode: toButtonWidthMode(button.width),
      fixedWidth: toButtonWidthMode(button.width) === "fixed" ? button.width : DEFAULT_FIXED_BUTTON_WIDTH,
      alignment: button.alignment || "left",
      backgroundColor: toHexColor(button.backgroundColor, colors),
      border: { ...button.border, color: toHexColor(button.border.color, colors) },
    },
    banner: { imageUrl: theme?.banner?.imageUrl ?? "", fileName: theme?.banner?.fileName ?? "" },
    layout: {
      contentArea: toPaddingValues(layout.contentArea),
      contentBlock: {
        group: contentBlock.group === "custom" ? "custom" : "all-blocks",
        allBlocks: toResponsivePaddingValues(contentBlock.allBlocks),
        headings: toResponsivePaddingValues(contentBlock.headings),
        paragraph: toResponsivePaddingValues(contentBlock.paragraph),
        button: toResponsivePaddingValues(contentBlock.button),
      },
    },
  };
};

const toSavedPadding = ({ left, right, top, bottom }: PaddingValues): APIPaddingOptions => ({ left, right, top, bottom });

const toSavedResponsivePadding = ({ mobile, desktop }: ResponsivePaddingValues): APILayoutResponsivePadding => ({
  mobile: toSavedPadding(mobile),
  desktop: toSavedPadding(desktop),
});

const toSavedTypography = ({
  fontFamily,
  fallbackFontFamily,
  fontSize,
  lineHeight,
  fontWeight,
  textColor,
}: TypographyValues): APIFormatBaseTypographyInput => ({
  fontFamily,
  fallbackFontFamily,
  fontSize,
  lineHeight,
  fontWeight,
  textColor,
});

/** Onto BEE's grid for a fixed button width; anything unreadable starts again from the default. */
export const toFixedButtonWidth = (value: string) => {
  const width = Number.parseFloat(value);

  if (Number.isNaN(width)) {
    return DEFAULT_FIXED_BUTTON_WIDTH;
  }

  const snapped = Math.round(width / FIXED_BUTTON_WIDTH_STEP) * FIXED_BUTTON_WIDTH_STEP;

  return String(Math.min(Math.max(snapped, MIN_FIXED_BUTTON_WIDTH), MAX_FIXED_BUTTON_WIDTH));
};

/** Snapped here as well as on blur: a save sent with Enter never leaves the field. */
const toSavedButtonWidth = ({ widthMode, fixedWidth }: ButtonValues) => {
  if (widthMode === "fill") {
    return FILL_BUTTON_WIDTH;
  }

  return widthMode === "fixed" ? toFixedButtonWidth(fixedWidth) : "auto";
};

const toSavedLayout = ({ contentArea, contentBlock }: LayoutValues): APIFormatThemeLayoutData => ({
  contentArea: toSavedPadding(contentArea),
  contentBlock: {
    group: contentBlock.group,
    allBlocks: toSavedResponsivePadding(contentBlock.allBlocks),
    headings: toSavedResponsivePadding(contentBlock.headings),
    paragraph: toSavedResponsivePadding(contentBlock.paragraph),
    button: toSavedResponsivePadding(contentBlock.button),
  },
});

/** The four sections in the legacy saved format, which BEE and the legacy page read as they are. */
export const formValuesToThemeSections = ({ typography, button, banner, layout }: EmailConfigurationFormValues) => ({
  typography: {
    heading: { ...toSavedTypography(typography.heading), alignment: typography.heading.alignment },
    subHeading: { ...toSavedTypography(typography.subHeading), alignment: typography.subHeading.alignment },
    paragraph: {
      ...toSavedTypography(typography.paragraph),
      alignment: typography.paragraph.alignment,
      linkColor: typography.paragraph.linkColor,
    },
    buttonLabel: toSavedTypography(typography.buttonLabel),
  },
  button: {
    padding: toSavedPadding(button.padding),
    width: toSavedButtonWidth(button),
    alignment: button.alignment,
    backgroundColor: button.backgroundColor,
    border: button.border,
  },
  banner,
  layout: toSavedLayout(layout),
});

/** The weights BEE was given for the font, or every weight while no font is chosen. */
export const getFontWeightOptions = (beeFont: BeeFont | undefined) => {
  const weights = beeFont?.fontWeight ? Object.keys(beeFont.fontWeight) : FONT_WEIGHTS;

  return weights.map((weight) => ({ label: FONT_WEIGHT_LABELS[weight] ?? weight, value: weight }));
};
